import React from 'react';
import PropTypes from 'prop-types';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  PieChart,
  Pie,
  AreaChart,
  Area,
} from 'recharts';

/**
 * Premium chart theme configuration
 */
const chartTheme = {
  colors: {
    primary: '#a78bfa',
    secondary: '#60a5fa',
    accent: '#f472b6',
    success: '#34d399',
    warning: '#fbbf24',
    error: '#f87171',
    grid: 'rgba(255, 255, 255, 0.04)',
    axis: 'rgba(255, 255, 255, 0.4)',
    axisLight: 'rgba(255, 255, 255, 0.2)',
  },
  gradients: {
    purple: ['#a78bfa', '#7c3aed'],
    blue: ['#60a5fa', '#2563eb'],
    green: ['#34d399', '#059669'],
    sunset: ['#fbbf24', '#f97316', '#ef4444'],
  },
  tooltip: {
    backgroundColor: 'rgba(15, 15, 20, 0.95)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    borderRadius: 12,
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
  },
};

/**
 * Premium Tooltip component for charts
 */
const PremiumTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;

  return (
    <div
      className="px-4 py-3 rounded-xl border"
      style={{
        backgroundColor: chartTheme.tooltip.backgroundColor,
        borderColor: chartTheme.tooltip.borderColor,
        boxShadow: chartTheme.tooltip.boxShadow,
      }}
    >
      {label && (
        <p className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">
          {label}
        </p>
      )}
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center gap-3">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: entry.color }}
          />
          <span className="text-white font-medium">
            {formatter ? formatter(entry.value, entry.name) : entry.value}
          </span>
          {entry.name && entry.name !== entry.dataKey && (
            <span className="text-gray-400 text-sm">{entry.name}</span>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * Premium Horizontal Bar Chart for skill distributions
 */
export const SkillDistributionChart = ({
  data,
  height = 280,
  showGrid = false,
  animate = true,
  className = '',
}) => {
  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
          barCategoryGap="20%"
        >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={chartTheme.colors.grid}
              horizontal={false}
            />
          )}
          <XAxis
            type="number"
            axisLine={false}
            tickLine={false}
            tick={{ fill: chartTheme.colors.axisLight, fontSize: 11 }}
          />
          <YAxis
            type="category"
            dataKey="level"
            axisLine={false}
            tickLine={false}
            tick={{ fill: chartTheme.colors.axis, fontSize: 12, fontWeight: 500 }}
            width={90}
          />
          <Tooltip content={<PremiumTooltip />} cursor={{ fill: 'rgba(255,255,255,0.02)' }} />
          <Bar
            dataKey="count"
            radius={[0, 6, 6, 0]}
            isAnimationActive={animate}
            animationDuration={800}
            animationEasing="ease-out"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color}
                fillOpacity={0.85}
                className="transition-all duration-300 hover:opacity-100"
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Premium Radar Chart for category overview
 */
export const CategoryRadarChart = ({
  data,
  dataKey = 'value',
  height = 300,
  color = 'primary',
  className = '',
}) => {
  const fillColor = chartTheme.colors[color] || chartTheme.colors.primary;

  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={data} margin={{ top: 20, right: 30, bottom: 20, left: 30 }}>
          <PolarGrid
            stroke={chartTheme.colors.grid}
            strokeWidth={1}
          />
          <PolarAngleAxis
            dataKey="category"
            tick={{
              fill: chartTheme.colors.axis,
              fontSize: 11,
              fontWeight: 500,
            }}
            tickLine={false}
          />
          <PolarRadiusAxis
            angle={90}
            domain={[0, 'auto']}
            tick={{ fill: chartTheme.colors.axisLight, fontSize: 10 }}
            axisLine={false}
            tickLine={false}
          />
          <Radar
            name="Skills"
            dataKey={dataKey}
            stroke={fillColor}
            strokeWidth={2}
            fill={fillColor}
            fillOpacity={0.25}
            dot={{
              r: 4,
              fill: fillColor,
              stroke: 'rgba(255,255,255,0.2)',
              strokeWidth: 2,
            }}
            activeDot={{
              r: 6,
              fill: fillColor,
              stroke: 'white',
              strokeWidth: 2,
            }}
          />
          <Tooltip content={<PremiumTooltip />} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Premium Donut Chart for composition views
 */
export const CompositionDonut = ({
  data,
  height = 200,
  innerRadius = 60,
  outerRadius = 80,
  centerLabel,
  centerValue,
  className = '',
}) => {
  return (
    <div className={`relative ${className}`} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            paddingAngle={2}
            dataKey="value"
            stroke="none"
            animationDuration={800}
            animationEasing="ease-out"
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={entry.color || chartTheme.colors.primary}
                className="transition-all duration-300"
              />
            ))}
          </Pie>
          <Tooltip content={<PremiumTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      {(centerLabel || centerValue) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          {centerValue && (
            <span className="text-2xl font-bold text-white">{centerValue}</span>
          )}
          {centerLabel && (
            <span className="text-xs text-gray-400 uppercase tracking-wide">{centerLabel}</span>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Premium Area Chart for trends
 */
export const TrendAreaChart = ({
  data,
  dataKey = 'value',
  height = 200,
  color = 'primary',
  showGrid = false,
  className = '',
}) => {
  const strokeColor = chartTheme.colors[color] || chartTheme.colors.primary;
  const gradientId = `gradient-${color}-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={className} style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={strokeColor} stopOpacity={0.3} />
              <stop offset="100%" stopColor={strokeColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          {showGrid && (
            <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.colors.grid} />
          )}
          <XAxis
            dataKey="name"
            axisLine={false}
            tickLine={false}
            tick={{ fill: chartTheme.colors.axisLight, fontSize: 10 }}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: chartTheme.colors.axisLight, fontSize: 10 }}
            width={40}
          />
          <Tooltip content={<PremiumTooltip />} />
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={strokeColor}
            strokeWidth={2}
            fill={`url(#${gradientId})`}
            animationDuration={800}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

/**
 * Skill Level Badge with visual prominence
 */
export const SkillLevelBadge = ({ level, size = 'sm', showLabel = true }) => {
  const levelConfig = {
    expert: { color: 'bg-success-500/20 text-success-400 border-success-500/30', label: 'Expert', icon: '★★★★★' },
    advanced: { color: 'bg-secondary-500/20 text-secondary-400 border-secondary-500/30', label: 'Advanced', icon: '★★★★' },
    intermediate: { color: 'bg-warning-500/20 text-warning-400 border-warning-500/30', label: 'Intermediate', icon: '★★★' },
    beginner: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', label: 'Beginner', icon: '★★' },
    novice: { color: 'bg-error-500/20 text-error-400 border-error-500/30', label: 'Novice', icon: '★' },
  };

  const config = levelConfig[level] || levelConfig.novice;

  const sizeClasses = {
    xs: 'text-[10px] px-1.5 py-0.5',
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  return (
    <span className={`inline-flex items-center gap-1 rounded-full border font-medium ${config.color} ${sizeClasses[size]}`}>
      {showLabel ? config.label : config.icon}
    </span>
  );
};

/**
 * Progress Arc for circular progress display
 */
export const ProgressArc = ({
  value,
  max = 100,
  size = 80,
  strokeWidth = 6,
  color = 'primary',
  label,
  className = '',
}) => {
  const percentage = Math.min((value / max) * 100, 100);
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percentage / 100) * circumference;

  const colorMap = {
    primary: '#a78bfa',
    secondary: '#60a5fa',
    success: '#34d399',
    warning: '#fbbf24',
    error: '#f87171',
  };

  const strokeColor = colorMap[color] || colorMap.primary;

  return (
    <div className={`relative inline-flex items-center justify-center ${className}`}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Background track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="rgba(255, 255, 255, 0.06)"
          strokeWidth={strokeWidth}
        />
        {/* Progress arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className="transition-all duration-700 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold text-white">{Math.round(percentage)}%</span>
        {label && <span className="text-[10px] text-gray-400 uppercase">{label}</span>}
      </div>
    </div>
  );
};

/**
 * Data Story Card - narrative wrapper for insights
 */
export const DataStoryCard = ({
  title,
  insight,
  metric,
  trend,
  trendLabel,
  children,
  className = '',
}) => {
  const trendColor = trend > 0 ? 'text-success-400' : trend < 0 ? 'text-error-400' : 'text-gray-400';
  const trendIcon = trend > 0 ? '↑' : trend < 0 ? '↓' : '→';

  return (
    <div className={`space-y-3 ${className}`}>
      {title && (
        <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">{title}</h4>
      )}
      {insight && (
        <p className="text-lg text-white leading-relaxed">{insight}</p>
      )}
      <div className="flex items-baseline gap-4">
        {metric && (
          <span className="text-4xl font-bold text-white tabular-nums">{metric}</span>
        )}
        {(trend !== undefined || trendLabel) && (
          <span className={`text-sm font-medium ${trendColor} flex items-center gap-1`}>
            {trend !== undefined && <span>{trendIcon} {Math.abs(trend)}%</span>}
            {trendLabel && <span className="text-gray-500">{trendLabel}</span>}
          </span>
        )}
      </div>
      {children}
    </div>
  );
};

DataStoryCard.propTypes = {
  title: PropTypes.string,
  insight: PropTypes.string,
  metric: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trend: PropTypes.number,
  trendLabel: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default {
  SkillDistributionChart,
  CategoryRadarChart,
  CompositionDonut,
  TrendAreaChart,
  SkillLevelBadge,
  ProgressArc,
  DataStoryCard,
};
